import type { AxiosInstance } from 'axios';
import { BASE_APP_PAth } from './launchpad-api';

const ADMIN_BASE = BASE_APP_PAth + '/api/admin';

// ─── Request / response models ────────────────────────────────────────────────

export interface CarrierViewRecord {
    id: number;
    carrierName: string;
    carrierCode: string;
    lineOfBusiness: string;
    stateCode: string;
    keyword: string;
    weight: number;
    keywordType: string;
    universalKeyPairId: string;
    primaryKeyword: string;
    secondaryKeyword: string;
    carrierBusinessClass: string;
    carrierBusinessClassId: number;
}

export interface CarrierViewResponse {
    data: CarrierViewRecord[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface CarrierViewRequest {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: string;
    searchTerm?: string;
    carrierFilter?: string;
    lobFilter?: string;
    stateFilter?: string;
}

export interface ChangeRequest {
    id: number;
    status: string;
    targetTable: string;
    targetRecordId: number | null;
    targetRecordIds: number[];
    changeData: Record<string, unknown>;
    requestedBy: string;
    requestedByEmail: string;
    requestedAt: string;
    reviewedBy: string;
    reviewedByEmail: string;
    reviewedAt: string | null;
    approvedBy: string;
    approvedByEmail: string;
    approvedAt: string | null;
    comments: string;
    rejectionReason: string;
    appliedAt: string | null;
    appliedBy: string;
}

export interface ChangeRequestListResponse {
    data: ChangeRequest[];
    totalCount: number;
}

export interface CreateChangeRequestPayload {
    targetTable: string;
    targetRecordId?: number | null;
    targetRecordIds?: number[];
    changeData: Record<string, unknown>;
    requestedBy: string;
    requestedByEmail: string;
    comments?: string;
    autoSubmit?: boolean;
}

export interface ChangeRequestActionPayload {
    comments?: string;
    actionBy: string;
    actionByEmail: string;
}

export interface ChangeRequestResponse {
    success: boolean;
    data: ChangeRequest;
    message: string;
}

export interface AuditLogEntry {
    id: number;
    action: string;
    tableName: string;
    recordId: number;
    oldValues: string;
    newValues: string;
    performedBy: string;
    performedByEmail: string;
    performedAt: string;
    changeRequestId: number | null;
}

export interface AuditLogResponse {
    data: AuditLogEntry[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface AuditLogRequest {
    page?: number;
    pageSize?: number;
    action?: string;
    tableName?: string;
    recordId?: number;
    performedBy?: string;
    startDate?: string;
    endDate?: string;
}

export interface AuditStatistics {
    totalChanges: number;
    changesThisMonth: number;
    pendingRequests: number;
    activeUsers: number;
    changesByTable: Record<string, number>;
    changesByAction: Record<string, number>;
}

export interface ReferenceCarrier {
    carrierId: number;
    carrierCode: string;
    carrierName: string;
    isActive: boolean;
}

export interface ReferenceLineOfBusiness {
    lineOfBusinessId: number;
    lobCode: string;
    lobName: string;
}

export interface ReferenceState {
    stateCode: string;
    stateName: string;
}

export interface ReferenceDataResponse<T> {
    data: T[];
}

export interface BulkTemplateRow {
    assocId: number;
    keywordId: number;
    keyword: string;
    primaryKeyword: string;
    secondaryKeyword: string;
    universalKeyPairId: number;
    stateCode: string;
    usStateId: number;
    businessClass: string | null;
}

export interface BulkTemplateResult {
    canDownload: boolean;
    message: string | null;
    existingCount: number;
    view: string;
    carrierId: number;
    carrierName: string;
    lineOfBusinessId: number;
    lineOfBusiness: string;
    rows: BulkTemplateRow[];
}

// ─── Auth header helper ───────────────────────────────────────────────────────

const authHeaders = (authToken: string) => ({
    'Content-type': 'application/json',
    'Authorization': `Bearer ${authToken}`,
});

// ─── Factory — mirrors launchPadAPI(api) pattern ──────────────────────────────

export const adminApi = (api: AxiosInstance) => ({

    getCarrierViewClassic: (params: CarrierViewRequest, authToken: string) =>
        api.get<CarrierViewResponse>(`${ADMIN_BASE}/carrier-recommendations/classic`, {
            params,
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getCarrierViewUniversal: (params: CarrierViewRequest, authToken: string) =>
        api.get<CarrierViewResponse>(`${ADMIN_BASE}/carrier-recommendations/universal`, {
            params,
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getCarrierViewById: (id: number, authToken: string) =>
        api.get<CarrierViewRecord>(`${ADMIN_BASE}/carrier-recommendations/classic/${id}`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getCarrierViewUniversalById: (id: number, authToken: string) =>
        api.get<CarrierViewRecord>(`${ADMIN_BASE}/carrier-recommendations/universal/${id}`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getCarrierViewClassicByIds: (ids: number[], authToken: string) =>
        api.post<CarrierViewRecord[]>(`${ADMIN_BASE}/carrier-recommendations/classic/by-ids`, { ids }, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getCarrierViewUniversalByIds: (ids: number[], authToken: string) =>
        api.post<CarrierViewRecord[]>(`${ADMIN_BASE}/carrier-recommendations/universal/by-ids`, { ids }, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getChangeRequests: (status: string, authToken: string) =>
        api.get<ChangeRequestListResponse>(`${ADMIN_BASE}/change-requests`, {
            params: { status, pageSize: 200 },
            headers: authHeaders(authToken),
        }).then(r => r.data),

    createChangeRequest: (payload: CreateChangeRequestPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    submitChangeRequest: (id: number, payload: ChangeRequestActionPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests/${id}/submit`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    reviewChangeRequest: (id: number, payload: ChangeRequestActionPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests/${id}/review`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    approveChangeRequest: (id: number, payload: ChangeRequestActionPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests/${id}/approve`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    rejectChangeRequest: (id: number, payload: ChangeRequestActionPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests/${id}/reject`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    applyChangeRequest: (id: number, payload: ChangeRequestActionPayload, authToken: string) =>
        api.post<ChangeRequestResponse>(`${ADMIN_BASE}/change-requests/${id}/apply`, payload, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getAuditLogs: (params: AuditLogRequest, authToken: string) =>
        api.get<AuditLogResponse>(`${ADMIN_BASE}/audit/logs`, {
            params,
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getAuditStatistics: (authToken: string) =>
        api.get<AuditStatistics>(`${ADMIN_BASE}/audit/statistics`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getAuditExport: (params: AuditLogRequest, authToken: string) =>
        api.get<Blob>(`${ADMIN_BASE}/audit/export`, {
            params,
            headers: authHeaders(authToken),
            responseType: 'blob',
        }).then(r => r.data),

    getReferenceCarriers: (authToken: string) =>
        api.get<ReferenceDataResponse<ReferenceCarrier>>(`${ADMIN_BASE}/reference/carriers`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getReferenceLinesOfBusiness: (authToken: string) =>
        api.get<ReferenceDataResponse<ReferenceLineOfBusiness>>(`${ADMIN_BASE}/reference/lines-of-business`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getReferenceStates: (authToken: string) =>
        api.get<ReferenceDataResponse<ReferenceState>>(`${ADMIN_BASE}/reference/states`, {
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getBulkTemplate: (view: string, carrierId: number, lineOfBusinessId: number, states: string, authToken: string) =>
        api.get<BulkTemplateResult>(`${ADMIN_BASE}/bulk-template`, {
            params: { view, carrierId, lineOfBusinessId, states },
            headers: authHeaders(authToken),
        }).then(r => r.data),

    getReferenceKeywords: (lineOfBusiness: string, authToken: string) =>
        api.get<ReferenceDataResponse<string>>(`${ADMIN_BASE}/reference/keywords`, {
            params: { lineOfBusiness },
            headers: authHeaders(authToken),
        }).then(r => r.data),
});
